import { Link } from "react-router-dom";
import { MessageCircle, Users } from "lucide-react";
import { formatApiError } from "../api";
import type { AdminMemberMatch } from "./moderationMatches";
import { ModerationMatchReport } from "./ModerationMatchReport";

type ModerationMemberMatchesProps = {
  memberId: string;
  matches: AdminMemberMatch[];
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
};

export function ModerationMemberMatches({
  memberId,
  matches,
  isLoading = false,
  error,
  onRetry,
}: ModerationMemberMatchesProps) {
  const matchesUnavailable = Boolean(
    error
    && typeof error === "object"
    && error !== null
    && "statusCode" in error
    && (error as { statusCode?: number }).statusCode === 404,
  );

  if (isLoading) {
    return (
      <article className="account-card moderation-matches-card">
        <div className="account-card-head">
          <Users />
          <div>
            <h2>Matches</h2>
            <p>Introduction history for this member.</p>
          </div>
        </div>
        <p className="moderation-match-empty">Loading match history…</p>
      </article>
    );
  }

  if (error) {
    return (
      <article className="account-card moderation-matches-card">
        <div className="account-card-head">
          <Users />
          <div>
            <h2>Matches</h2>
            <p>Introduction history for this member.</p>
          </div>
        </div>
        <div className="moderation-match-empty moderation-match-empty--error">
          <p>
            {matchesUnavailable
              ? "Match history is not available yet. Deploy the latest API to api.beyara.app."
              : formatApiError(error)}
          </p>
          {onRetry && (
            <button type="button" className="text-link" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      </article>
    );
  }

  if (matches.length === 0) {
    return (
      <article className="account-card moderation-matches-card">
        <div className="account-card-head">
          <Users />
          <div>
            <h2>Matches</h2>
            <p>Introduction history for this member.</p>
          </div>
        </div>
        <p className="moderation-match-empty">No introductions yet.</p>
      </article>
    );
  }

  return (
    <article className="account-card moderation-matches-card">
      <div className="account-card-head">
        <Users />
        <div>
          <h2>Matches</h2>
          <p>Introduction history for this member.</p>
        </div>
      </div>

      <ul className="moderation-match-list">
        {matches.map((match) => (
          <li className="moderation-match-item" key={match.id}>
            <div className="moderation-match-item-main">
              <strong>{match.otherFirstName}</strong>
              <span className="moderation-match-status">{match.statusLabel}</span>
            </div>
            {match.canViewChat ? (
              <Link
                className="moderation-match-action"
                to={`/admin/users/${memberId}/matches/${match.id}/chat`}
              >
                <MessageCircle size={14} aria-hidden="true" />
                View chat
              </Link>
            ) : (
              <span className="moderation-match-action-spacer" aria-hidden="true" />
            )}

            {(match.reports ?? []).map((report) => (
              <ModerationMatchReport key={report.id} memberId={memberId} report={report} />
            ))}
          </li>
        ))}
      </ul>
    </article>
  );
}
